import { useIsModalOpen } from '@codegouvfr/react-dsfr/Modal/useIsModalOpen';
import { SIZE_10MO_OCTETS, useTextes } from '@common/config';
import { useSessionContext } from '@common/hooks';
import { useDepositOrganismeEtablissementFichierMutation } from '@common/services';
import { EtablissementDeclarantDto } from '@common/services/etablissement';
import { EtablissementFichierType } from '@common/services/etablissement-fichier';
import {
  DsfrModal,
  EXCEL_EXTENSIONS,
  EXCEL_FILE_TYPES,
  FileUploadField,
  Form,
  FormMethodsRef,
  SelectField,
  SelectOption,
} from '@common/ui';
import { appToast } from '@common/ui/toast';
import { fetchLocalFile } from '@common/utils/file';
import { useRef, useState } from 'react';
import { z } from 'zod';
import { depotDeclarationFormSchema } from '../schemas/depotDeclaration.schema';

enum DeclarationError {
  FICHIER_ALERTE = 'FICHIER_ALERTE',
  CORROMPU = 'CORROMPU',
  EXTERNE = 'EXTERNE',
}

export const DepotDeclarationModal = ({ dsfrModal, etablissements }: DepotDeclarationModalProps) => {
  const t = useTextes('ParcoursOfa', 'Campagne');
  const buttonSubmitRef = useRef<HTMLButtonElement | null>(null);
  const formRef = useRef<FormMethodsRef<DepotDeclarationFormResult> | null>(null);
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useIsModalOpen(dsfrModal, {
    onConceal: () => formRef.current?.reset(),
  });

  const [declarantId, setDeclarantId] = useState<string>(etablissements[0].id.toString()); // Le premier de la liste est forcément principal, au vu du tri de la réponse
  const { organismeId } = useSessionContext();

  const [depositEtablissementFichier, { isLoading: isDeposing }] = useDepositOrganismeEtablissementFichierMutation();

  const declarantsOptions: SelectOption[] = etablissements.map(({ id, denominationUsuelle }) => ({
    label: denominationUsuelle,
    value: id.toString(),
  }));

  const values = {
    etablissementDeclarant: declarantId,
    fichierDeclaration: null,
  };

  const handleClose = () => {
    dsfrModal.close();
    setInfoMessage('');
    setErrorMessage('');
    formRef.current?.reset();
  };

  const handleSuccess = () => {
    setInfoMessage(t['DETAIL_OFA_DOC_MES2']);
    setErrorMessage('');
    formRef.current?.reset();
  };

  const handleError = (e: unknown) => {
    if (e.status === 400) {
      const [error] = e.data.errors;
      if (error.type === DeclarationError.FICHIER_ALERTE) setErrorMessage(t['DETAIL_OFA_DOC_MES1']);
      else if (error.type === DeclarationError.CORROMPU) setErrorMessage(t['DETAIL_OFA_DOC_MES3']);
      else if (error.type === DeclarationError.EXTERNE) setErrorMessage(t['DETAIL_OFA_DOC_MES4']);
      formRef.current?.reset();
    } else if (e.status === 500) {
      const { error } = e.data;
      appToast.error(error);
      formRef.current?.reset();
    }
  };

  const handleSubmit = async (result: DepotDeclarationFormResult) => {
    const file: File | null = await fetchLocalFile(result.declaration.url, result.declaration.name);
    if (!file) return appToast.error('Le dépôt de la déclaration est obligatoire.');

    const formData = new FormData();
    formData.set('fichier', file);

    setInfoMessage('');
    setErrorMessage('');

    try {
      await depositEtablissementFichier({
        id: organismeId,
        etablissementId: Number(declarantId),
        fichierType: EtablissementFichierType.DECLARATION,
        formData,
      }).unwrap();
      handleSuccess();
    } catch (err) {
      handleError(err);
    }
  };

  return (
    <dsfrModal.Component
      title={'Déposer ma déclaration'}
      className={'text-left'}
      size="large"
      topAnchor={false}
      buttons={[
        {
          children: 'ANNULER',
          className: 'text-red-400!',
          doClosesModal: true,
          priority: 'tertiary',
          onClick: handleClose,
        },
        {
          children: 'ENVOYER',
          doClosesModal: false,
          priority: 'primary',
          onClick: () => buttonSubmitRef.current?.click(),
          disabled: isDeposing,
        },
      ]}>

      <Form<DepotDeclarationFormResult>
        className={'flex flex-col gap-2'}
        schema={depotDeclarationFormSchema}
        onSubmit={handleSubmit}
        buttonSubmitRef={buttonSubmitRef}
        methodsRef={formRef}
        values={values}>

        <SelectField name={'etablissementDeclarant'}
                     options={declarantsOptions}
                     title={'Etablissement déclarant'}
                     onSelect={(id) => setDeclarantId(id)}
                     disabled={(declarantsOptions.length === 1)} />

        <FileUploadField name={'declaration'}
                         extensions={EXCEL_EXTENSIONS}
                         fileTypes={EXCEL_FILE_TYPES}
                         maxSize={SIZE_10MO_OCTETS}
                         requiredErrorMsg={t['DEPOT_FICHIER_TEXTE1']}
                         typesSelection={['Déclaration']} />

        <span className={'color-secondary'}>{infoMessage}</span>
        <span className={'color-error'}>{errorMessage}</span>

      </Form>
    </dsfrModal.Component>
  );
};

interface DepotDeclarationModalProps {
  dsfrModal: DsfrModal;
  etablissements: EtablissementDeclarantDto[];
}

type DepotDeclarationFormResult = z.infer<typeof depotDeclarationFormSchema>